import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Check } from "lucide-react";
import { api, formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function ArtistDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, items } = useCart();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/artists/${slug}`).then((r) => setArtist(r.data)).catch(() => setArtist(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="pt-40 text-center text-zinc-500">Chargement…</div>;
  if (!artist)
    return (
      <div className="pt-40 text-center text-zinc-400 min-h-screen">
        Artiste introuvable. <Link to="/artistes" className="text-[#60a5fa] underline">Voir les artistes</Link>
      </div>
    );

  return (
    <div className="pt-28 pb-24 min-h-screen" data-testid="artist-detail-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <button onClick={() => navigate("/artistes")} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-10 transition-colors" data-testid="back-artists">
          <ArrowLeft className="h-4 w-4" /> Tous les artistes
        </button>

        {/* Artist hero */}
        <div className="grid lg:grid-cols-5 gap-12 items-start mb-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2 aspect-[3/4] overflow-hidden bg-[#0d0d0d]"
          >
            {artist.photo ? (
              <img src={artist.photo} alt={artist.name} className="h-full w-full object-cover" data-testid="artist-photo" />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-zinc-700 font-display text-8xl">{artist.name.charAt(0)}</div>
            )}
          </motion.div>

          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.3em] text-[#60a5fa] mb-4">Artiste</p>
            <h1 className="font-display text-5xl md:text-6xl text-white tracking-tight" data-testid="artist-name">{artist.name}</h1>
            {artist.style && (
              <p className="text-lg text-[#60a5fa] mt-3" data-testid="artist-style">{artist.style}</p>
            )}
            {artist.personality && (
              <div className="mt-8">
                <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Sa personnalité</h3>
                <p className="text-zinc-200 leading-relaxed text-lg italic font-display" data-testid="artist-personality">« {artist.personality} »</p>
              </div>
            )}
            {artist.bio && (
              <div className="mt-8">
                <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Parcours</h3>
                <p className="text-zinc-400 leading-relaxed">{artist.bio}</p>
              </div>
            )}
          </div>
        </div>

        {/* Artworks */}
        <h2 className="font-display text-4xl text-white tracking-tight mb-10">Ses œuvres</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {artist.artworks.map((a, i) => {
            const inCart = items.find((it) => it.id === a.id);
            return (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                data-testid={`artist-artwork-${a.id}`}
              >
                <Link to={`/oeuvre/${a.id}`} className="block overflow-hidden bg-[#0d0d0d] relative aspect-[4/5] group">
                  <img src={a.image_url} alt={a.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  {a.sold && (
                    <div className="absolute top-4 left-4 bg-[#E11D48] text-white text-[10px] uppercase tracking-widest px-3 py-1">Vendu</div>
                  )}
                </Link>
                <div className="mt-4">
                  <div className="flex items-start justify-between gap-3">
                    <Link to={`/oeuvre/${a.id}`}>
                      <h3 className="font-display text-xl text-white hover:text-[#60a5fa] transition-colors">{a.title}</h3>
                    </Link>
                    {!a.sold && (
                      <button
                        data-testid={`artist-add-cart-${a.id}`}
                        onClick={() => addItem(a)}
                        className="shrink-0 h-9 w-9 flex items-center justify-center border border-white/15 text-white hover:bg-[#2563EB] hover:border-[#2563EB] transition-all"
                        aria-label="Ajouter au panier"
                      >
                        {inCart ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                  {a.dimensions && <p className="text-zinc-500 text-sm mt-1">{a.dimensions}{a.technique ? ` · ${a.technique}` : ""}</p>}
                  {a.description && <p className="text-zinc-400 text-sm mt-2 line-clamp-2">{a.description}</p>}
                  <p className="text-white text-sm mt-2">{formatPrice(a.price)}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
